import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { Menu as MenuPrimitive } from "@base-ui/react/menu";
import { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import { Tabs as TabsPrimitive } from "@base-ui/react/tabs";
import { cva } from "class-variance-authority";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { usePortalHost } from "../../providers/portal-host";
import { Button } from "../button";
import { FilterChip } from "../chip";
import { Icon, type IconName } from "../icon";
import { IconButton } from "../icon-button";
import {
  MenuCheckboxItem,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuTrigger,
} from "../menu";
import { useTriggerMouseDown } from "../use-trigger-mouse-down";

export interface FilterOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export type FilterValue = string | null;
export type FilterMenuAlign = "start" | "center" | "end";

function useControlledArrayValue({
  value,
  defaultValue,
  onChange,
}: {
  value?: string[];
  defaultValue?: string[];
  onChange?: (value: string[]) => void;
}) {
  const [internalValue, setInternalValue] = useState<string[]>(
    defaultValue ?? [],
  );
  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;

  function setValue(nextValue: string[]) {
    if (!isControlled) setInternalValue(nextValue);
    onChange?.(nextValue);
  }

  return [currentValue, setValue] as const;
}

function useControlledStringValue({
  value,
  defaultValue,
  onChange,
}: {
  value?: FilterValue;
  defaultValue?: FilterValue;
  onChange?: (value: FilterValue) => void;
}) {
  const [internalValue, setInternalValue] = useState<FilterValue>(
    defaultValue ?? null,
  );
  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;

  function setValue(nextValue: FilterValue) {
    if (!isControlled) setInternalValue(nextValue);
    onChange?.(nextValue);
  }

  return [currentValue, setValue] as const;
}

const filterButtonVariants = cva(
  [
    "inline-flex",
    "h-(--spacing-32)",
    "items-center",
    "justify-center",
    "gap-(--spacing-8)",
    "rounded-(--radius-sm)",
    "border",
    "border-solid",
    "border-button-outline-default-border",
    "bg-button-outline-default-background",
    "px-(--spacing-12)",
    "font-(family-name:--font-family-label)",
    "text-(length:--font-size-body-medium)",
    "font-(number:--font-weight-medium)",
    "leading-(--font-line-height-20)",
    "text-button-outline-default-text",
    "outline-none",
    "transition-colors",
    "duration-200",
    "ease-in-out",
    "cursor-pointer",
    "select-none",
    "hover:border-button-outline-hover-border",
    "hover:bg-button-outline-hover-background",
    "active:border-button-outline-pressed-border",
    "active:bg-button-outline-pressed-background",
    "data-[mouse-down]:border-button-outline-pressed-border",
    "data-[mouse-down]:bg-button-outline-pressed-background",
    "focus-visible:ring-2",
    "focus-visible:ring-offset-[3px]",
    "focus-visible:ring-offset-surface",
    "focus-visible:ring-button-focus-ring",
    "disabled:pointer-events-none",
    "disabled:cursor-not-allowed",
    "disabled:opacity-40",
    "[&_svg]:pointer-events-none",
    "[&_svg]:shrink-0",
  ],
  {
    variants: {
      fullWidth: {
        true: "w-full",
        false: "",
      },
    },
    defaultVariants: {
      fullWidth: false,
    },
  },
);

interface FilterButtonProps
  extends Omit<ButtonPrimitive.Props, "className" | "children"> {
  children: React.ReactNode;
  leadingIcon?: IconName;
  trailingIcon?: IconName;
  showChevron?: boolean;
  open?: boolean;
  mouseDown?: boolean;
  fullWidth?: boolean;
  className?: string;
}

function FilterButton({
  children,
  leadingIcon,
  trailingIcon,
  showChevron = false,
  open = false,
  mouseDown = false,
  fullWidth = false,
  className,
  ...props
}: FilterButtonProps) {
  return (
    <ButtonPrimitive
      className={cn(filterButtonVariants({ fullWidth }), className)}
      {...props}
      data-mouse-down={mouseDown ? "" : undefined}
      aria-expanded={showChevron ? open : props["aria-expanded"]}
    >
      {leadingIcon && <Icon name={leadingIcon} size="xs" tone="inherit" />}
      <span className="min-w-0 truncate">{children}</span>
      {trailingIcon && <Icon name={trailingIcon} size="xs" tone="inherit" />}
      {showChevron && (
        <span
          data-open={open ? "" : undefined}
          className="inline-flex transition-transform duration-200 data-[open]:rotate-180"
        >
          <Icon name="chevron-down" size="xs" tone="inherit" />
        </span>
      )}
    </ButtonPrimitive>
  );
}

export interface FilterPanelRenderProps {
  /** Closes the panel from custom body content. */
  close: () => void;
}

export type FilterPanelChildren =
  | React.ReactNode
  | ((props: FilterPanelRenderProps) => React.ReactNode);

export interface FilterPanelProps {
  /** Panel body content, or a render function receiving `{ close }`. */
  children: FilterPanelChildren;
  /** Panel title. Defaults to "Filters". */
  title?: string;
  /** Trigger button label. When omitted, the trigger renders as an icon button. */
  triggerLabel?: string;
  /** Horizontal alignment of the panel relative to the trigger. */
  align?: "start" | "end";
  /** Controlled open state. */
  open?: boolean;
  /** Initial open state for uncontrolled usage. */
  defaultOpen?: boolean;
  /** Called when open state changes. */
  onOpenChange?: (open: boolean) => void;
  /** Called when Apply filters is clicked. The panel closes after this callback. */
  onApply?: () => void;
  /** Label for the apply action button. */
  applyLabel?: string;
  /** Called when Clear All is clicked. The panel stays open. */
  onClearAll?: () => void;
  /** Label for the clear-all action button. */
  clearAllLabel?: string;
  /** Disables the trigger button. */
  disabled?: boolean;
}

function FilterPanel({
  children,
  title = "Filters",
  triggerLabel,
  align = "start",
  open,
  defaultOpen,
  onOpenChange,
  onApply,
  applyLabel = "Apply filters",
  onClearAll,
  clearAllLabel = "Clear All",
  disabled,
}: FilterPanelProps) {
  const portalHost = usePortalHost();
  const [internalOpen, setInternalOpen] = useState(defaultOpen ?? false);
  const { mouseDown, triggerMouseDownProps } = useTriggerMouseDown();
  const isControlled = open !== undefined;
  const currentOpen = isControlled ? open : internalOpen;

  function setOpen(nextOpen: boolean) {
    if (!isControlled) setInternalOpen(nextOpen);
    onOpenChange?.(nextOpen);
  }

  function close() {
    setOpen(false);
  }

  return (
    <PopoverPrimitive.Root
      open={currentOpen}
      onOpenChange={setOpen}
      modal={false}
    >
      <PopoverPrimitive.Trigger
        render={
          triggerLabel !== undefined ? (
            <FilterButton
              trailingIcon="filter"
              disabled={disabled}
              mouseDown={mouseDown}
              {...triggerMouseDownProps}
            >
              {triggerLabel}
            </FilterButton>
          ) : (
            <IconButton
              variant="outline"
              size="sm"
              icon="filter"
              aria-label={title}
              disabled={disabled}
              {...triggerMouseDownProps}
            />
          )
        }
      />
      <PopoverPrimitive.Portal container={portalHost}>
        <PopoverPrimitive.Positioner side="bottom" align={align} sideOffset={4}>
          <PopoverPrimitive.Popup
            role="dialog"
            aria-label={title}
            className={cn(
              "flex",
              "w-[500px]",
              "max-w-[calc(100vw-var(--spacing-32))]",
              "max-h-[544px]",
              "flex-col",
              "gap-(--spacing-20)",
              "overflow-y-auto",
              "rounded-(--radius-sm)",
              "border",
              "border-solid",
              "border-outline-subtle",
              "bg-surface",
              "p-(--spacing-20)",
              "shadow-elevation-3",
              "outline-none",
            )}
          >
            <div className="flex items-center justify-between gap-(--spacing-16)">
              <h2 className="font-(family-name:--font-family-title) text-(length:--font-size-title-lg) font-(number:--font-weight-bold) leading-(--font-line-height-28) text-text-default">
                {title}
              </h2>
              {onClearAll && (
                <Button variant="ghost" size="sm" onClick={onClearAll}>
                  {clearAllLabel}
                </Button>
              )}
            </div>
            <div className="flex flex-col gap-(--spacing-16)">
              {typeof children === "function" ? children({ close }) : children}
            </div>
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={() => {
                  onApply?.();
                  close();
                }}
              >
                {applyLabel}
              </Button>
            </div>
          </PopoverPrimitive.Popup>
        </PopoverPrimitive.Positioner>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

export interface SingleFilterProps {
  label: string;
  options: FilterOption[];
  value?: string[];
  defaultValue?: string[];
  onChange?: (value: string[]) => void;
  /** Alignment of the menu relative to the trigger. Defaults to "start". */
  align?: FilterMenuAlign;
  disabled?: boolean;
}

function SingleFilter({
  label,
  options,
  value,
  defaultValue,
  onChange,
  align = "start",
  disabled,
}: SingleFilterProps) {
  const [selectedValues, setSelectedValues] = useControlledArrayValue({
    value,
    defaultValue,
    onChange,
  });
  const [open, setOpen] = useState(false);
  const { mouseDown, triggerMouseDownProps } = useTriggerMouseDown();

  function handleCheckedChange(optionValue: string, checked: boolean) {
    setSelectedValues(
      checked
        ? [...selectedValues, optionValue]
        : selectedValues.filter(
            (selectedValue) => selectedValue !== optionValue,
          ),
    );
  }

  return (
    <MenuPrimitive.Root open={open} onOpenChange={setOpen}>
      <MenuTrigger
        render={
          <FilterButton
            showChevron
            open={open}
            disabled={disabled}
            mouseDown={mouseDown}
            {...triggerMouseDownProps}
          >
            {label}
          </FilterButton>
        }
      />
      <MenuPopup size="auto" align={align}>
        {options.map((option) => (
          <MenuCheckboxItem
            key={option.value}
            checked={selectedValues.includes(option.value)}
            disabled={option.disabled}
            closeOnClick={false}
            onCheckedChange={(checked) => {
              handleCheckedChange(option.value, checked);
            }}
          >
            {option.label}
          </MenuCheckboxItem>
        ))}
      </MenuPopup>
    </MenuPrimitive.Root>
  );
}

export interface DateRangeFilterProps {
  options: FilterOption[];
  value?: FilterValue;
  defaultValue?: FilterValue;
  onChange?: (value: FilterValue) => void;
  placeholder?: string;
  /** Alignment of the menu relative to the trigger. Defaults to "start". */
  align?: FilterMenuAlign;
  disabled?: boolean;
}

function DateRangeFilter({
  options,
  value,
  defaultValue,
  onChange,
  placeholder = "Date Range",
  align = "start",
  disabled,
}: DateRangeFilterProps) {
  const [selectedValue, setSelectedValue] = useControlledStringValue({
    value,
    defaultValue,
    onChange,
  });
  const [open, setOpen] = useState(false);
  const { mouseDown, triggerMouseDownProps } = useTriggerMouseDown();
  const selectedLabel =
    options.find((option) => option.value === selectedValue)?.label ??
    placeholder;

  return (
    <MenuPrimitive.Root open={open} onOpenChange={setOpen}>
      <MenuTrigger
        render={
          <FilterButton
            leadingIcon="calendar"
            showChevron
            open={open}
            mouseDown={mouseDown}
            disabled={disabled}
            {...triggerMouseDownProps}
          >
            {selectedLabel}
          </FilterButton>
        }
      />
      <MenuPopup size="trigger" align={align}>
        <MenuRadioGroup
          value={selectedValue ?? ""}
          onValueChange={(nextValue) => {
            setSelectedValue(nextValue);
            setOpen(false);
          }}
        >
          {options.map((option) => (
            <MenuRadioItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </MenuPopup>
    </MenuPrimitive.Root>
  );
}

export interface SortGroupFilterProps {
  prefix: string;
  options: FilterOption[];
  value?: FilterValue;
  defaultValue?: FilterValue;
  onChange?: (value: FilterValue) => void;
  /** Alignment of the menu relative to the trigger. Defaults to "start". */
  align?: FilterMenuAlign;
  disabled?: boolean;
}

function SortGroupFilter({
  prefix,
  options,
  value,
  defaultValue,
  onChange,
  align = "start",
  disabled,
}: SortGroupFilterProps) {
  const [selectedValue, setSelectedValue] = useControlledStringValue({
    value,
    defaultValue,
    onChange,
  });
  const [open, setOpen] = useState(false);
  const { mouseDown, triggerMouseDownProps } = useTriggerMouseDown();
  const selectedLabel =
    options.find((option) => option.value === selectedValue)?.label ?? "";

  return (
    <MenuPrimitive.Root open={open} onOpenChange={setOpen}>
      <MenuTrigger
        render={
          <FilterButton
            showChevron
            open={open}
            disabled={disabled}
            mouseDown={mouseDown}
            {...triggerMouseDownProps}
          >
            <span className="text-text-subtle">{prefix}</span>
            {selectedLabel && (
              <>
                {" "}
                <span>{selectedLabel}</span>
              </>
            )}
          </FilterButton>
        }
      />
      <MenuPopup size="trigger" align={align}>
        <MenuRadioGroup
          value={selectedValue ?? ""}
          onValueChange={(nextValue) => {
            setSelectedValue(nextValue);
            setOpen(false);
          }}
        >
          {options.map((option) => (
            <MenuRadioItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </MenuPopup>
    </MenuPrimitive.Root>
  );
}

export interface SelectedFilterItem {
  id: string;
  label: string;
  removeAriaLabel?: string;
}

export interface SelectedFiltersProps {
  filters: SelectedFilterItem[];
  onRemove: (id: string) => void;
  onClearAll: () => void;
  label?: string;
}

function SelectedFilters({
  filters,
  onRemove,
  onClearAll,
  label = "Filters:",
}: SelectedFiltersProps) {
  if (filters.length === 0) return null;

  return (
    <div
      aria-live="polite"
      className="flex flex-wrap items-center gap-(--spacing-8)"
    >
      <span className="font-(family-name:--font-family-label) text-(length:--font-size-label-medium) font-(number:--font-weight-semibold) leading-(--font-line-height-20) text-text-subtle">
        {label}
      </span>
      {filters.map((filter) => (
        <FilterChip
          key={filter.id}
          label={filter.label}
          removeAriaLabel={filter.removeAriaLabel}
          onClose={() => onRemove(filter.id)}
        />
      ))}
      <Button variant="ghost" size="sm" onClick={onClearAll}>
        Clear All
      </Button>
    </div>
  );
}

export interface SegmentedFilterItem {
  value: string;
  label: string;
  count?: number;
  disabled?: boolean;
}

export interface SegmentedFilterProps {
  items: SegmentedFilterItem[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  "aria-label"?: string;
}

function SegmentedFilter({
  items,
  value,
  defaultValue,
  onValueChange,
  "aria-label": ariaLabel = "Filter views",
}: SegmentedFilterProps) {
  return (
    <TabsPrimitive.Root
      value={value}
      defaultValue={defaultValue}
      onValueChange={(nextValue) => onValueChange?.(nextValue)}
    >
      <TabsPrimitive.List
        aria-label={ariaLabel}
        className={cn(
          "inline-flex",
          "flex-wrap",
          "max-w-full",
          "items-center",
          "gap-[2px]",
          "rounded-(--radius-sm)",
          "border",
          "border-solid",
          "border-outline-subtle",
          "bg-surface-container-high",
          "p-[2px]",
          "max-[599px]:gap-(--spacing-4)",
          "max-[599px]:p-(--spacing-4)",
        )}
      >
        {items.map((item) => (
          <TabsPrimitive.Tab
            key={item.value}
            value={item.value}
            disabled={item.disabled}
            aria-label={
              item.count === undefined
                ? item.label
                : `${item.label} ${item.count}`
            }
            className={cn(
              "relative",
              "group",
              "inline-flex",
              "h-[28px]",
              "min-w-0",
              "items-center",
              "justify-center",
              "gap-(--spacing-6)",
              "rounded-(--radius-xs)",
              "px-(--spacing-10)",
              "font-(family-name:--font-family-label)",
              "text-(length:--font-size-body-medium)",
              "font-(number:--font-weight-medium)",
              "leading-(--font-line-height-20)",
              "text-text-subtle",
              "outline-none",
              "transition-[background-color,box-shadow]",
              "duration-200",
              "ease-in-out",
              "cursor-pointer",
              "select-none",
              "focus-visible:[outline:2px_solid_hsl(var(--button-focus-ring))]",
              "focus-visible:[outline-offset:2px]",
              "data-[active]:bg-surface-container-lowest",
              "data-[active]:text-text-default",
              "data-[active]:shadow-elevation-1",
              "data-[disabled]:pointer-events-none",
              "data-[disabled]:cursor-default",
              "data-[disabled]:opacity-50",
              "max-[599px]:before:absolute",
              "max-[599px]:before:left-0",
              "max-[599px]:before:top-1/2",
              "max-[599px]:before:h-[44px]",
              "max-[599px]:before:min-w-[44px]",
              "max-[599px]:before:w-full",
              "max-[599px]:before:-translate-y-1/2",
              "max-[599px]:before:content-['']",
            )}
          >
            <span className="truncate">{item.label}</span>
            {item.count !== undefined && (
              <span className="text-text-disabled group-data-[active]:text-text-subtle">
                {item.count}
              </span>
            )}
          </TabsPrimitive.Tab>
        ))}
      </TabsPrimitive.List>
    </TabsPrimitive.Root>
  );
}

export {
  DateRangeFilter,
  FilterPanel,
  SegmentedFilter,
  SelectedFilters,
  SingleFilter,
  SortGroupFilter,
};
